<script lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

/**
 * UM interruptor por tela. A conversa e o copiloto abertos lado a lado montam-no
 * os dois, e a mesma chave global aparecia duas vezes; fica o montado por último
 * (a moldura que a pessoa acabou de abrir), e o anterior volta quando ele sai.
 */
const mounted = ref<symbol[]>([]);
</script>

<script setup lang="ts">
import Icon from "@/design/primitives/Icon.vue";
import { useI18n } from "vue-i18n";
import { previewMessages } from "./i18n/preview";
import { useAgentDisplayMode } from "./use-agent-display-mode";

/**
 * ESPREITAR A COLUNA DO CLIENTE — e o caminho de volta, que nunca se esconde.
 *
 * ── O DEFEITO QUE ISTO DESFAZ ─────────────────────────────────────────────
 *
 * O interruptor morava DENTRO do cartão de atividade, no rodapé que só existe
 * quando há tokens ou custo para mostrar. Clicar em "ver como cliente" apagava
 * esses números — é exactamente o que o modo cliente não mostra —, o rodapé
 * deixava de existir e o botão ia com ele. Numa fala sem ferramentas e sem
 * raciocínio (o caso do dono: `nsteps=0`) o cartão inteiro desaparecia, e a
 * preferência ficava gravada no browser: recarregar voltava em modo cliente, sem
 * um único botão na tela para sair de lá.
 *
 * Um controlo não pode viver dentro daquilo que ele desliga.
 *
 * ── ONDE ELE VIVE AGORA ───────────────────────────────────────────────────
 *
 * Em `shared/` e não em `features/agents`, porque a QUARTA superfície é de outra
 * feature: a conversa com o cliente, na inbox. Ele governa `useAgentDisplayMode`, que
 * já morava aqui, e `no-cross-feature` impedia a inbox de o importar de lá.
 *
 * Montado na moldura da conversa do Ben (`StreamingChat` — a gaveta, a coluna da
 * inbox e a tela de agentes) e no cabeçalho da conversa com o cliente. Em ambos
 * existe sempre. É um afford de ADMIN: aparece por `canPreview` (é admin de plataforma,
 * e a instalação permite espreitar) e nunca por `audience`. Simular um cliente
 * não pode alterar o que o administrador tem à mão, ou a simulação prende-o.
 *
 * A V2 tinha-o numa tela de definições (`dev-settings.vue`) — fora do que ele
 * controla, pela mesma razão. Aqui fica ao lado da conversa em vez de a três
 * cliques, porque é a olhar para uma resposta que se quer ver a outra coluna.
 */
// O ÚNICO `useI18n` do arquivo — um segundo escopo local é ignorado em silêncio e
// a tela mostra a chave crua. Ver `shared/i18n/two-scopes.spec.ts`.
const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: previewMessages });
/** Só o botão redondo, sem a faixa: a conversa do Ben, onde a faixa sumia sob o cabeçalho. */
defineProps<{ compact?: boolean }>();
const { canPreview, previewing, setPreviewAsClient } = useAgentDisplayMode();
const me = Symbol("preview-as-client");
onMounted(() => mounted.value.push(me));
onUnmounted(() => {
	mounted.value = mounted.value.filter((one) => one !== me);
});
const shown = computed(() => mounted.value[mounted.value.length - 1] === me);
</script>

<template>
	<button
		v-if="canPreview && shown && compact"
		type="button"
		class="flex size-8 items-center justify-center rounded-full border shadow-sm backdrop-blur focus-visible:outline-2 focus-visible:outline-accent"
		:class="previewing ? 'border-warning/50 bg-warning-subtle text-warning' : 'border-border bg-surface/80 text-fg-subtle hover:text-fg'"
		:aria-pressed="previewing"
		:aria-label="previewing ? t('preview.asDev') : t('preview.asClient')"
		:title="previewing ? `${t('preview.previewing')} · ${t('preview.asDev')}` : t('preview.asClient')"
		@click="setPreviewAsClient(!previewing)"
	>
		<Icon :name="previewing ? 'eye' : 'eyeOff'" class="size-4" />
	</button>
	<div
		v-else-if="canPreview && shown"
		class="flex shrink-0 items-center gap-2 px-3 py-1 text-2xs"
		:class="
			previewing
				? 'border-b border-warning/40 bg-warning-subtle text-fg'
				: 'border-b border-border text-fg-subtle'
		"
	>
		<!-- A simulação DIZ-SE. Sem esta linha, o painel do admin fica com menos
		     informação do que devia e nada explica porquê. -->
		<span v-if="previewing" class="flex min-w-0 items-center gap-1 truncate"><Icon name="eye" class="size-3.5" />{{ t("preview.previewing") }}</span>
		<button
			type="button"
			class="ml-auto shrink-0 underline hover:text-fg"
			@click="setPreviewAsClient(!previewing)"
		>
			{{ previewing ? t("preview.asDev") : t("preview.asClient") }}
		</button>
	</div>
</template>
